import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ImageUp } from 'lucide-react';
import { useRef } from 'react';
import { api } from '../lib/api';
import { apiErrorMessage, useToast } from '../lib/ToastContext';
import { assetUrl, uploadBrandingLogo } from '../lib/upload';
import { Loader } from '../components/ui/Spinner';

type Branding = { logoUrl: string | null };

// The only branding field today is the logo — used on the payslip PDF header (and anywhere
// else in-app branding is added later). Deliberately a single-field page, not a generic
// "settings" form, matching this app's "no generic CRUD" rule (product-spec.md §5.5).
export function BrandingSettingsPage() {
  const client = useQueryClient();
  const { success, error: toastError } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const branding = useQuery({ queryKey: ['branding'], queryFn: async () => (await api.get<Branding>('/api/branding')).data });

  const upload = useMutation({
    mutationFn: (file: File) => uploadBrandingLogo(file),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['branding'] }); success('Logo updated'); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not upload logo')),
  });

  return (
    <>
      <h1 className="mb-6 text-3xl font-bold">Branding</h1>
      <div className="card max-w-md">
        <p className="mb-3 font-semibold">Company logo</p>
        <p className="mb-4 text-sm text-muted">Shown on the header of downloaded payslip PDFs. JPEG, PNG, or WebP, up to 2MB.</p>
        <div className="mb-4 flex h-24 items-center justify-center rounded-xl border border-dashed border-border bg-canvas">
          {branding.isLoading ? (
            <Loader className="!py-4" />
          ) : branding.data?.logoUrl ? (
            <img src={assetUrl(branding.data.logoUrl)} alt="Company logo" className="max-h-20 max-w-full object-contain" />
          ) : (
            <p className="text-sm text-muted">No logo uploaded yet</p>
          )}
        </div>
        <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => { const file = e.target.files?.[0]; if (file) upload.mutate(file); e.target.value = ''; }} />
        <button className="btn-primary" disabled={upload.isPending} onClick={() => inputRef.current?.click()}>
          <ImageUp size={16} /> {upload.isPending ? 'Uploading…' : branding.data?.logoUrl ? 'Replace logo' : 'Upload logo'}
        </button>
      </div>
    </>
  );
}
